import type { EarningsCard } from "../types/cards";

export const earningsCards: EarningsCard[] = [
  {
    id: "earn_001",
    title: "Freelance Web Project",
    description: "A client pays you to build their website.",
    conceptExplanation: "Freelancing lets you sell skills directly. Having a portfolio (degree or skill badge) lets you charge premium rates — employers and clients pay more for proven expertise.",
    deck: "earnings",
    illustrationKey: "blue",
    baseIncome: 30,
    requiredIcons: ["degree", "skill_badge"],
    multiplier: 1.5,
    wlbCost: 2,
  },
  {
    id: "earn_002",
    title: "Overtime Shift",
    description: "Your boss needs extra hours covered.",
    conceptExplanation: "Overtime pays more per hour but costs energy. Overworking long-term leads to burnout — watch your Work-Life Balance! More money isn't always worth it.",
    deck: "earnings",
    illustrationKey: "orange",
    baseIncome: 20,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 3,
  },
  {
    id: "earn_003",
    title: "Skilled Trades Job",
    description: "A plumbing contract comes in.",
    conceptExplanation: "Trade certifications qualify you for high-demand jobs that pay well without a four-year degree. Electricians, plumbers, and welders are always in demand.",
    deck: "earnings",
    illustrationKey: "yellow",
    baseIncome: 40,
    requiredIcons: ["trade_cert"],
    multiplier: 2.0,
    wlbCost: 2,
  },
  {
    id: "earn_004",
    title: "Team Lead Promotion",
    description: "Your leadership got noticed.",
    conceptExplanation: "Leadership skills are often worth more than technical ability alone. Senior roles with management responsibilities command higher salaries and benefits.",
    deck: "earnings",
    illustrationKey: "green",
    baseIncome: 50,
    requiredIcons: ["leadership", "degree"],
    multiplier: 1.75,
    wlbCost: 1,
  },
  {
    id: "earn_005",
    title: "Language Tutoring",
    description: "Teach your second language online.",
    conceptExplanation: "Speaking a second language opens doors to global clients and international roles. Bilingual workers earn on average 10–15% more than their monolingual peers.",
    deck: "earnings",
    illustrationKey: "teal",
    baseIncome: 25,
    requiredIcons: ["language"],
    multiplier: 1.6,
    wlbCost: 1,
  },
  {
    id: "earn_006",
    title: "Entry-Level Office Job",
    description: "A steady paycheck every month.",
    conceptExplanation: "Steady income is the foundation of financial stability. Even a modest regular income lets you budget, save, and invest over time.",
    deck: "earnings",
    illustrationKey: "gray",
    baseIncome: 18,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 1,
  },
  {
    id: "earn_007",
    title: "Consulting Gig",
    description: "A company needs your expertise.",
    conceptExplanation: "Consultants charge for specialized knowledge. The more rare your skills, the more you can charge. Networking often brings these opportunities.",
    deck: "earnings",
    illustrationKey: "blue",
    baseIncome: 45,
    requiredIcons: ["network", "degree"],
    multiplier: 1.8,
    wlbCost: 2,
  },
  {
    id: "earn_008",
    title: "Weekend Market Stall",
    description: "Sell handmade goods at the local market.",
    conceptExplanation: "A side hustle can supplement your main income. Small businesses often start this way — testing an idea before going full-time.",
    deck: "earnings",
    illustrationKey: "pink",
    baseIncome: 15,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 2,
  },
  {
    id: "earn_009",
    title: "Software Engineer Role",
    description: "A tech company hires you full-time.",
    conceptExplanation: "Technical roles in software command some of the highest salaries in the job market. A skill badge or degree dramatically improves your chances of landing them.",
    deck: "earnings",
    illustrationKey: "blue",
    baseIncome: 55,
    requiredIcons: ["skill_badge", "degree"],
    multiplier: 1.6,
    wlbCost: 1,
  },
  {
    id: "earn_010",
    title: "Delivery Driver",
    description: "Flexible hours, instant pay.",
    conceptExplanation: "Gig work offers flexibility but no benefits like health insurance or paid leave. It's great short-term but consider what you're giving up long-term.",
    deck: "earnings",
    illustrationKey: "orange",
    baseIncome: 12,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 2,
  },
  {
    id: "earn_011",
    title: "International Sales Deal",
    description: "Close a deal with an overseas client.",
    conceptExplanation: "Global business skills are increasingly valuable. Companies pay premiums for employees who can bridge cultural and language gaps to close deals abroad.",
    deck: "earnings",
    illustrationKey: "teal",
    baseIncome: 60,
    requiredIcons: ["language", "network"],
    multiplier: 2.0,
    wlbCost: 2,
  },
  {
    id: "earn_012",
    title: "Lawn Mowing Round",
    description: "Help neighbours with their gardens.",
    conceptExplanation: "A great first job for kids! Trading your time for money is the simplest form of earning. As you build skills, you can charge more and work smarter.",
    deck: "earnings",
    illustrationKey: "green",
    baseIncome: 8,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 2,
  },
  {
    id: "earn_013",
    title: "Skilled Electrician Call-Out",
    description: "Emergency electrical repair job.",
    conceptExplanation: "Emergency trades workers earn premium rates because they're available when others aren't. A trade cert plus availability is a powerful income combination.",
    deck: "earnings",
    illustrationKey: "yellow",
    baseIncome: 50,
    requiredIcons: ["trade_cert"],
    multiplier: 1.8,
    wlbCost: 2,
  },
  {
    id: "earn_014",
    title: "Referral Bonus",
    description: "You recommended a colleague — score!",
    conceptExplanation: "A strong network pays off in unexpected ways. Many companies offer referral bonuses because they trust employees to recommend quality candidates.",
    deck: "earnings",
    illustrationKey: "pink",
    baseIncome: 22,
    requiredIcons: ["network"],
    multiplier: 1.5,
    wlbCost: 0,
  },
  {
    id: "earn_015",
    title: "Research Grant",
    description: "Awarded funding for your research project.",
    conceptExplanation: "Advanced degrees open doors to research funding, grants, and fellowships — income sources that don't exist without the credential.",
    deck: "earnings",
    illustrationKey: "purple",
    baseIncome: 35,
    requiredIcons: ["degree"],
    multiplier: 1.7,
    wlbCost: 1,
  },
  {
    id: "earn_016",
    title: "Babysitting",
    description: "Look after the kids next door.",
    conceptExplanation: "Responsibility earns trust — and trust earns money. Reliable babysitters get repeat clients and referrals, building a small business from a simple service.",
    deck: "earnings",
    illustrationKey: "pink",
    baseIncome: 10,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 1,
  },
  {
    id: "earn_017",
    title: "Podcast Sponsorship",
    description: "Your podcast landed a paid sponsor.",
    conceptExplanation: "Content creation is a modern income stream. Building an audience first takes time, but once you have it, sponsors pay for access to your listeners — passive-feeling income from active work.",
    deck: "earnings",
    illustrationKey: "purple",
    baseIncome: 28,
    requiredIcons: ["network", "skill_badge"],
    multiplier: 1.6,
    wlbCost: 1,
  },
  {
    id: "earn_018",
    title: "Real Estate Showing",
    description: "Help a family find their dream home.",
    conceptExplanation: "Real estate agents earn commission — a percentage of the sale price. A single deal can equal months of salary. Commission-based work has high highs and unpredictable lows.",
    deck: "earnings",
    illustrationKey: "green",
    baseIncome: 45,
    requiredIcons: ["network", "leadership"],
    multiplier: 1.7,
    wlbCost: 2,
  },
  {
    id: "earn_019",
    title: "Stock Photo Sale",
    description: "Your photos sold on a stock platform.",
    conceptExplanation: "Royalty income means you earn every time someone uses your creation — without more work from you. This is one of the most powerful forms of passive income.",
    deck: "earnings",
    illustrationKey: "teal",
    baseIncome: 12,
    requiredIcons: ["skill_badge"],
    multiplier: 1.4,
    wlbCost: 0,
  },
  {
    id: "earn_020",
    title: "Tutoring Session",
    description: "Help a student pass their exam.",
    conceptExplanation: "Teaching what you know is one of the oldest ways to earn. The more qualified you are, the more you can charge — and online platforms now let tutors reach students worldwide.",
    deck: "earnings",
    illustrationKey: "blue",
    baseIncome: 22,
    requiredIcons: ["degree"],
    multiplier: 1.5,
    wlbCost: 1,
  },
  {
    id: "earn_021",
    title: "Catering Gig",
    description: "Cook and serve at a private event.",
    conceptExplanation: "Food businesses have low barriers to entry. A catering side hustle can grow into a full business — many restaurant owners started by cooking for friends and events.",
    deck: "earnings",
    illustrationKey: "orange",
    baseIncome: 20,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 3,
  },
  {
    id: "earn_022",
    title: "App Development Contract",
    description: "A startup hires you to build their MVP.",
    conceptExplanation: "Contract software work pays premium rates because it's project-based, not salaried. Developers with a portfolio can charge day rates that equal weeks of office pay.",
    deck: "earnings",
    illustrationKey: "blue",
    baseIncome: 65,
    requiredIcons: ["skill_badge", "degree"],
    multiplier: 1.8,
    wlbCost: 3,
  },
  {
    id: "earn_023",
    title: "Translation Work",
    description: "Translate a business document.",
    conceptExplanation: "Professional translators earn significantly more than casual interpreters. Specialising in legal, medical or technical translation commands premium rates.",
    deck: "earnings",
    illustrationKey: "teal",
    baseIncome: 30,
    requiredIcons: ["language", "degree"],
    multiplier: 1.7,
    wlbCost: 1,
  },
  {
    id: "earn_024",
    title: "Vintage Resale",
    description: "Flip thrift-store finds online.",
    conceptExplanation: "Buy low, sell high — the core of trading. Resellers learn to spot undervalued items and sell where demand is highest. This skill applies equally to stocks, real estate and businesses.",
    deck: "earnings",
    illustrationKey: "pink",
    baseIncome: 18,
    requiredIcons: [],
    multiplier: 1.0,
    wlbCost: 2,
  },
];
